import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, of, ReplaySubject, take } from 'rxjs';
import { AppLanguage, LanguageService } from './language.service';

/**
 * Switches the UI language: loads its translations, then publishes it through LanguageService
 * (page direction, `lang` sent to the API, routed page re-created by MainComponent).
 * Read the current language from LanguageService and translate with TranslateService.
 */
@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly readySubject = new ReplaySubject<void>(1);
  /** Emits once the translations of the first language are loaded: pages can then use `instant()`. */
  readonly ready$: Observable<void> = this.readySubject.asObservable();

  /** Last language asked for: a slower load of a previous choice must not override it. */
  private requested?: AppLanguage;

  constructor(private translate: TranslateService,
              private languageService: LanguageService) {
  }

  init(): void {
    this.translate.setDefaultLang('fr');
    this.setLanguage(this.languageService.current);
  }

  setLanguage(lang: string): void {
    const language = LanguageService.normalize(lang);
    this.requested = language;
    this.translate
      .use(language)
      .pipe(take(1), catchError(() => of(null)))
      .subscribe(() => {
        if (language !== this.requested) {
          // ngx-translate switched to this late language when it loaded: switch back to the one asked for
          this.translate.use(this.requested!);
          return;
        }
        this.languageService.set(language);
        this.readySubject.next();
      });
  }
}
